import { ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { getDisplayAmount } from "@/lib/currency";
import { getSubscriptionPeriodBreakdown } from "@/lib/totals";
import { cn } from "@/lib/utils";

interface YearlySpendDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptions: DatabaseSubscription[];
  currentPeriod: { month: number; year: number };
  displayCurrency: string;
  rates?: Record<string, number>;
  paymentHistory?: Array<{
    subscription_id: string;
    payment_date: string;
    status: string;
  }>;
  onEditSubscription?: (subscriptionId: string) => void;
  includePaygInStats?: boolean;
}

type SortField = "name" | "amount" | "cycle" | "payments" | "status" | "total";
type SortDirection = "asc" | "desc";

export const YearlySpendDetailsDialog = ({
  open,
  onOpenChange,
  subscriptions,
  currentPeriod,
  displayCurrency,
  rates,
  paymentHistory,
  onEditSubscription,
  includePaygInStats,
}: YearlySpendDetailsDialogProps) => {
  const [sortField, setSortField] = useState<SortField>("total");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const breakdown = useMemo(() => {
    const yearStart = new Date(currentPeriod.year, 0, 1);
    const yearEnd = new Date(currentPeriod.year, 11, 31, 23, 59, 59);

    return getSubscriptionPeriodBreakdown(
      subscriptions,
      yearStart,
      yearEnd,
      {
        includePayg: includePaygInStats ?? false,
        excludeCancelled: true,
        displayCurrency,
        rates,
      },
      paymentHistory,
    );
  }, [
    subscriptions,
    currentPeriod.year,
    displayCurrency,
    rates,
    paymentHistory,
    includePaygInStats,
  ]);

  const sortedBreakdown = useMemo(() => {
    return [...breakdown].sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortField) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "amount":
          aVal = a.amount;
          bVal = b.amount;
          break;
        case "cycle":
          aVal = a.billingCycle;
          bVal = b.billingCycle;
          break;
        case "payments":
          aVal = a.paymentCount;
          bVal = b.paymentCount;
          break;
        case "status":
          aVal = a.displayReason;
          bVal = b.displayReason;
          break;
        case "total":
          aVal = a.convertedTotal;
          bVal = b.convertedTotal;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [breakdown, sortField, sortDirection]);

  const total = useMemo(() => {
    return breakdown.reduce((sum, item) => sum + item.convertedTotal, 0);
  }, [breakdown]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Yearly Spend Breakdown - {currentPeriod.year}
          </DialogTitle>
          <DialogDescription>
            Charges in {currentPeriod.year}.
          </DialogDescription>
        </DialogHeader>

        {breakdown.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-4xl">📊</div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">
              No charges this year
            </h3>
            <p className="text-muted-foreground">
              No charges in {currentPeriod.year}.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right"
                    onClick={() => handleSort("amount")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Amount
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("cycle")}
                  >
                    <div className="flex items-center gap-1">
                      Cycle
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-center"
                    onClick={() => handleSort("payments")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Payments
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-center"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Status
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right"
                    onClick={() => handleSort("total")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Total
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedBreakdown.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => onEditSubscription?.(item.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onEditSubscription?.(item.id);
                      }
                    }}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{item.name}</span>
                        {item.endsInPeriod && item.endDate && (
                          <Badge variant="warning" className="text-xs">
                            Ends{" "}
                            {new Date(item.endDate).toLocaleDateString(
                              "default",
                              { month: "short", day: "numeric" },
                            )}
                          </Badge>
                        )}
                        {includePaygInStats &&
                          item.billingType === "pay_as_you_go" && (
                            <Badge
                              variant="outline"
                              className="border-purple-300 bg-purple-100 text-xs text-purple-800 dark:border-purple-700 dark:bg-purple-900 dark:text-purple-200"
                            >
                              PAYG
                            </Badge>
                          )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {
                        getDisplayAmount(
                          item.amount,
                          item.currency,
                          item.currency,
                        ).formatted
                      }
                    </TableCell>
                    <TableCell>{item.billingCycle}</TableCell>
                    <TableCell className="text-center">
                      {item.paymentCount}
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          item.displayReason === "Paid" &&
                            "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
                          item.displayReason === "Upcoming" &&
                            "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
                          item.displayReason === "Due" &&
                            "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
                        )}
                      >
                        {item.displayReason}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {
                        getDisplayAmount(
                          item.convertedTotal,
                          displayCurrency,
                          displayCurrency,
                        ).formatted
                      }
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 bg-muted/50">
                  <TableCell colSpan={5} className="text-right font-bold">
                    Total
                  </TableCell>
                  <TableCell className="text-right text-lg font-bold">
                    {
                      getDisplayAmount(total, displayCurrency, displayCurrency)
                        .formatted
                    }
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
